import { type CommentItem, ReportCommentRequest } from '@app/_proto/Protos/comments';
import { CommentsClient } from '@app/_proto/Protos/comments.client';
import { ReactComponent as CloseIcon } from '@app/assets/x.svg';
import ActivateButton from '@app/domains/common/components/button/ActivateButton';
import CancelButton from '@app/domains/common/components/button/CancelButton';
import RadioGroup from '@app/domains/common/containers/RadioGroup';
import useToast from '@app/domains/common/hooks/useToast';
import { GrpcError } from '@app/libs/grpc';
import { useHttp } from '@app/libs/http';
import { batch } from '@app/libs/utils';
import { Alert, AlertTitle, Dialog } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCallback, useState } from 'react';

//#region : Main component

/** Custom. Main component parameters type */
export type Props = {
    reportOpen: boolean;
    reportComment: CommentItem | null;
    onReportSubmitted: () => void;
    onReportCancelled: () => void;
};

/**
 * Custom.
 *
 * Displays dialog to report comment and also sends comment report to server.
 */
export default function CommentReport({ reportOpen, reportComment, onReportSubmitted, onReportCancelled }: Props) {
    //#region : Variables, functions and api calls

    //General
    const [reportContent, setReportContent] = useState(REPORT_REASONS[0]);
    const [reportError, setReportError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const { open: openToast } = useToast({});

    //#region : Server request to post comment report
    const { grpcRequest } = useHttp();
    const queryClient = useQueryClient();
    const onReportComment = useCallback(async () => {
        if (!reportComment) {
            return;
        }

        setDisabled(true);

        try {
            const request = ReportCommentRequest.create({
                id: reportComment.id,
                content: reportContent,
            });

            const result = await grpcRequest(CommentsClient, c => c.reportComment, request);

            batch(() => {
                setReportContent(REPORT_REASONS[0]);
                setReportError('');
            });

            if (result) {
                await queryClient.invalidateQueries(['comments']);
                openToast({ text: 'Thank you. We’ve received your report.' });
                onReportSubmitted?.();
            }
        } catch (e) {
            if (e instanceof GrpcError) {
                const error = e;

                setReportError(error.response?.description || error.message);
            }
        } finally {
            setDisabled(false);
        }
    }, [reportComment, reportContent, grpcRequest, queryClient, openToast, onReportSubmitted]);
    //#endregion : Server request to post comment report

    //#endregion : Variables, functions and api calls

    if (!reportComment) {
        return null;
    }

    return (
        <Dialog
            open={reportOpen}
            onClose={onReportCancelled}
            aria-labelledby="form-dialog-title"
            classes={{ paper: 'm-8 sm:max-w-[480px]' }}
        >
            <div className="bg-gray-bg-elevate p-20">
                <button
                    className="absolute right-2 top-2 rounded-full p-10 text-gray-line-emphasis hover:bg-black/[.04] dark:hover:bg-white/[.08]"
                    aria-label="close"
                    onClick={onReportCancelled}
                >
                    <CloseIcon />
                </button>
                <h3 className="font-set-b24 pt-16 text-gray-t1">Why do you want to report this comment?</h3>
                <p className="font-set-m15 py-12 text-gray-t3">Comment by {reportComment?.poster?.userName}</p>
                <div className="font-set-sb16 -mr-4 py-12">
                    <RadioGroup
                        name="report-reason"
                        onChange={e => setReportContent((e.target as HTMLInputElement).id)}
                    >
                        {REPORT_REASONS.map((reason, idx) => (
                            <RadioItem key={reason} value={reason} defaultChecked={idx === 0} />
                        ))}
                    </RadioGroup>
                </div>
                {reportError?.length > 0 && (
                    <Alert severity="error" onClose={() => setReportError('')}>
                        <AlertTitle>Error</AlertTitle>
                        <div className="text-[16px]" dangerouslySetInnerHTML={{ __html: reportError }}></div>
                    </Alert>
                )}
                <div className="flex pt-12">
                    <CancelButton
                        className="font-set-sb14 mr-12 grow"
                        classes={{ text: 'py-10' }}
                        onClick={onReportCancelled}
                        disabled={disabled}
                    >
                        CANCEL
                    </CancelButton>
                    <ActivateButton
                        className="font-set-sb14 grow"
                        classes={{ text: 'py-10' }}
                        onClick={onReportComment}
                        disabled={disabled}
                    >
                        SUBMIT
                    </ActivateButton>
                </div>
            </div>
        </Dialog>
    );
}

type RadioItemProps = {
    value: string;
    defaultChecked?: boolean;
};
const RadioItem = ({ value, defaultChecked }: RadioItemProps) => (
    <RadioGroup.Radio
        value={value}
        defaultChecked={defaultChecked}
        classes={{
            root: 'flex items-center py-4',
            label: 'py-15 pr-8 grow',
            input: 'w-24 h-24 form-radio checked:text-blue-600 bg-transparent checked:bg-none checked:p-6 checked:bg-clip-content border-none !ring-inset !ring-[1.5px] !ring-gray-assist !ring-offset-0',
        }}
    />
);

const REPORT_REASONS = [
    'Spoilers',
    'Abuse or harassment',
    'Spam',
    'Copyright infringement',
    'Discrimination (racism, sexism, etc.)',
    'Request to delete a comment that you created',
];
